"use client";

import {
  createEventDialogOpenAtom,
  createEventEndTimeAtom,
  createEventResourceAtom,
  createEventStartTimeAtom,
} from "@/atoms/calendarAtoms";
import { Event, Group, Resource, Workspace } from "@/db/schema";
import { cn } from "@/lib/utils";
import clsx from "clsx";
import { useAtom } from "jotai";
import CalendarEvents from "./CalendarEvents";
import { CalendarTitle } from "./CalendarTitle";

export function ResourceColumn({
  workspace,
  resource,
  resourceIndex,
  events,
  from,
  to,
  standalone = false,
  group,
}: {
  workspace: Workspace;
  resource: Resource;
  resourceIndex: number;
  events: Event[];
  from: Date;
  to: Date;
  standalone?: boolean;
  group: Group;
}) {
  const [, setCreateDialogOpen] = useAtom(createEventDialogOpenAtom);
  const [, setSelectedResource] = useAtom(createEventResourceAtom);
  const [, setStartTime] = useAtom(createEventStartTimeAtom);
  const [, setEndTime] = useAtom(createEventEndTimeAtom);

  const dayLength = group.closeHour - group.openHour;
  const blocksPerHour = 2;

  const handleBlockClick = (index: number) => {
    const hour = Math.floor(index / 2) + group.openHour;
    const minute = (index % 2) * 30;
    const newStartTime = new Date(from);
    newStartTime.setHours(hour, minute);

    const newEndTime = new Date(newStartTime);
    newEndTime.setMinutes(newEndTime.getMinutes() + 30);

    setStartTime(newStartTime);
    setEndTime(newEndTime);
    setSelectedResource(resource);
    setCreateDialogOpen(true);
  };

  // Function to check if a time slot has an event
  const hasEventAtTimeSlot = (blockIndex: number) => {
    // Convert block index to time
    const hour = Math.floor(blockIndex / blocksPerHour) + group.openHour;
    const minute = (blockIndex % blocksPerHour) * 30;

    // Create a date object for the current block
    const blockTimeStart = new Date(from);
    blockTimeStart.setHours(hour, minute, 0, 0);

    // Calculate block end time (30 minutes or whatever your interval is)
    const blockTimeEnd = new Date(blockTimeStart);
    blockTimeEnd.setMinutes(blockTimeEnd.getMinutes() + 60 / blocksPerHour);

    // Check if any event overlaps with this time slot
    return events.some((event) => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);

      // Check for overlap
      return (
        (eventStart <= blockTimeStart && eventEnd > blockTimeStart) || // Event starts before and ends after block start
        (eventStart >= blockTimeStart && eventStart < blockTimeEnd) // Event starts during the block
      );
    });
  };

  return (
    <div
      style={{
        gridColumn: resourceIndex + 2,
      }}
      className={cn(`snap-start flex flex-col md:w-auto ${standalone ? "w-[100w]" : "w-[90vw] "}`)}
    >
      <CalendarTitle resource={resource} group={group} />
      <div
        className="day-column-wrapper grid grid-cols-[1fr_10fr] md:grid-cols-1 divide-y divide-gray-100 px-2 md:px-0"
        style={{
          gridTemplateRows: `repeat(${dayLength * blocksPerHour}, var(--block-height))`,
        }}
      >
        {/* BLOCKS */}
        {Array.from({ length: dayLength * blocksPerHour }, (_, index) =>
          !hasEventAtTimeSlot(index) ? (
            <div
              onClick={() => {
                handleBlockClick(index);
              }}
              style={{ gridRow: index + 1, height: "var(--block-height)" }}
              className={clsx(
                "col-span-1 md:col-start-1 col-start-2",
                "hover:bg-gray-100 cursor-pointer",
              )}
              key={index}
            />
          ) : (
            <div
              key={index}
              className="col-start-2 md:col-start-1"
              style={{ gridRow: index + 1 }}
            />
          ),
        )}
        {/* TIMES */}
        <div
          className="grid col-start-1 col-end-2 row-start-1 md:hidden"
          style={{
            gridTemplateRows: `repeat(${dayLength * blocksPerHour}, calc(var(--block-height) * 2))`,
          }}
        >
          {Array.from({ length: dayLength }).map((_, index) => (
            <p key={index} className=" text-xs text-gray-500 text-end pr-1 pt-1">
              {(group.openHour + index).toString().padStart(2, "0")}:00
            </p>
          ))}
        </div>
        {/* EVENTS */}
        <CalendarEvents events={events} workspace={workspace} group={group} />
      </div>
    </div>
  );
}
